"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import { useLanguage } from "@/lib/language-context"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  MessageCircle,
  Headphones,
  Shield,
  Zap,
  ChevronDown,
} from "lucide-react"
import { api } from "@/lib/api"

export function ContactContent() {
  const { t, siteSettings, currentLang } = useLanguage()
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState("")
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)

  // Scroll animation
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set())
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({})

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [])

  const translations = {
    uz: {
      hero_badge: "Biz bilan bog'laning",
      hero_title: "Aloqa",
      hero_subtitle: "Savollaringiz va takliflaringiz uchun biz doimo tayyormiz",
      stats_response: "Tezkor javob",
      stats_response_value: "1 soat",
      stats_support: "Qo'llab-quvvatlash",
      stats_support_value: "24/7",
      stats_satisfaction: "Mijozlar mamnuniyati",
      stats_satisfaction_value: "99%",
      contact_info_title: "Aloqa ma'lumotlari",
      contact_info_subtitle: "Biz bilan bog'lanishning qulay usullarini tanlang",
      phone_label: "Telefon raqam",
      email_label: "Email manzil",
      address_label: "Manzil",
      working_hours: "Ish vaqti",
      working_hours_value: "Dushanba - Shanba: 9:00 - 21:00",
      form_title: "Xabar yuborish",
      form_subtitle: "Savollaringizni yuboring, biz tez orada javob beramiz",
      form_name: "Ismingiz",
      form_name_placeholder: "Ismingizni kiriting",
      form_phone: "Telefon raqam",
      form_phone_placeholder: "+998 90 123 45 67",
      form_submit: "Yuborish",
      form_submitting: "Yuborilmoqda...",
      form_success_title: "Xabar yuborildi!",
      form_success_text: "Tez orada siz bilan bog'lanamiz",
      form_send_another: "Yana xabar yuborish",
      features_title: "Nega bizni tanlash kerak?",
      feature_1_title: "Tezkor javob",
      feature_1_desc: "1 soat ichida javob beramiz",
      feature_2_title: "Professional yordam",
      feature_2_desc: "Malakali mutaxassislar",
      feature_3_title: "Ishonchlilik",
      feature_3_desc: "15 yillik tajriba",
      feature_4_title: "Qulay xizmat",
      feature_4_desc: "24/7 qo'llab-quvvatlash",
      faq_title: "Ko'p beriladigan savollar",
      faq_1_q: "Mahsulotlar sertifikatlangan mi?",
      faq_1_a: "Ha, barcha mahsulotlarimiz tegishli davlat organlaridan sertifikat olgan.",
      faq_2_q: "Yetkazib berish qancha vaqt oladi?",
      faq_2_a: "Toshkent shahri bo'ylab 1-2 soat, viloyatlarga 1-3 kun ichida.",
      faq_3_q: "To'lov qanday amalga oshiriladi?",
      faq_3_a: "Naqd pul, bank kartasi yoki Click/Payme orqali.",
      cta_title: "Savollaringiz bormi?",
      cta_text: "Biz bilan bog'laning va professional maslahat oling",
      cta_button: "Qo'ng'iroq qilish",
    },
    en: {
      hero_badge: "Contact Us",
      hero_title: "Contact",
      hero_subtitle: "We are always ready for your questions and suggestions",
      stats_response: "Quick Response",
      stats_response_value: "1 hour",
      stats_support: "Support",
      stats_support_value: "24/7",
      stats_satisfaction: "Customer Satisfaction",
      stats_satisfaction_value: "99%",
      contact_info_title: "Contact Information",
      contact_info_subtitle: "Choose convenient ways to contact us",
      phone_label: "Phone",
      email_label: "Email",
      address_label: "Address",
      working_hours: "Working Hours",
      working_hours_value: "Monday - Saturday: 9:00 - 21:00",
      form_title: "Send Message",
      form_subtitle: "Send your questions, we will respond shortly",
      form_name: "Your Name",
      form_name_placeholder: "Enter your name",
      form_phone: "Phone Number",
      form_phone_placeholder: "+998 90 123 45 67",
      form_submit: "Send",
      form_submitting: "Sending...",
      form_success_title: "Message Sent!",
      form_success_text: "We will contact you shortly",
      form_send_another: "Send Another Message",
      features_title: "Why Choose Us?",
      feature_1_title: "Quick Response",
      feature_1_desc: "Response within 1 hour",
      feature_2_title: "Professional Help",
      feature_2_desc: "Qualified specialists",
      feature_3_title: "Reliability",
      feature_3_desc: "15 years of experience",
      feature_4_title: "Convenient Service",
      feature_4_desc: "24/7 support",
      faq_title: "Frequently Asked Questions",
      faq_1_q: "Are products certified?",
      faq_1_a: "Yes, all our products are certified by relevant authorities.",
      faq_2_q: "How long does delivery take?",
      faq_2_a: "Within Tashkent 1-2 hours, to regions 1-3 days.",
      faq_3_q: "How to pay?",
      faq_3_a: "Cash, bank card, or via Click/Payme.",
      cta_title: "Have Questions?",
      cta_text: "Contact us and get professional consultation",
      cta_button: "Call Now",
    },
    ru: {
      hero_badge: "Свяжитесь с нами",
      hero_title: "Контакты",
      hero_subtitle: "Мы всегда готовы ответить на ваши вопросы и предложения",
      stats_response: "Быстрый ответ",
      stats_response_value: "1 час",
      stats_support: "Поддержка",
      stats_support_value: "24/7",
      stats_satisfaction: "Удовлетворенность клиентов",
      stats_satisfaction_value: "99%",
      contact_info_title: "Контактная информация",
      contact_info_subtitle: "Выберите удобный способ связи",
      phone_label: "Телефон",
      email_label: "Эл. почта",
      address_label: "Адрес",
      working_hours: "Время работы",
      working_hours_value: "Понедельник - Суббота: 9:00 - 21:00",
      form_title: "Отправить сообщение",
      form_subtitle: "Отправьте ваши вопросы, мы ответим в ближайшее время",
      form_name: "Ваше имя",
      form_name_placeholder: "Введите ваше имя",
      form_phone: "Номер телефона",
      form_phone_placeholder: "+998 90 123 45 67",
      form_submit: "Отправить",
      form_submitting: "Отправка...",
      form_success_title: "Сообщение отправлено!",
      form_success_text: "Мы свяжемся с вами в ближайшее время",
      form_send_another: "Отправить еще сообщение",
      features_title: "Почему выбирают нас?",
      feature_1_title: "Быстрый ответ",
      feature_1_desc: "Ответ в течение 1 часа",
      feature_2_title: "Профессиональная помощь",
      feature_2_desc: "Квалифицированные специалисты",
      feature_3_title: "Надежность",
      feature_3_desc: "15 лет опыта",
      feature_4_title: "Удобный сервис",
      feature_4_desc: "Поддержка 24/7",
      faq_title: "Часто задаваемые вопросы",
      faq_1_q: "Продукция сертифицирована?",
      faq_1_a: "Да, вся наша продукция сертифицирована соответствующими органами.",
      faq_2_q: "Сколько времени занимает доставка?",
      faq_2_a: "По Ташкенту 1-2 часа, в регионы 1-3 дня.",
      faq_3_q: "Как оплатить?",
      faq_3_a: "Наличными, банковской картой или через Click/Payme.",
      cta_title: "Есть вопросы?",
      cta_text: "Свяжитесь с нами и получите профессиональную консультацию",
      cta_button: "Позвонить",
    },
  }

  const ct = translations[currentLang as keyof typeof translations] || translations.uz

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError("")

    const result = await api.sendContact({
      name: formData.name,
      phone: formData.phone,
    })

    setIsSubmitting(false)

    if (result.success) {
      setIsSubmitted(true)
    } else {
      setError(result.error || "Xatolik yuz berdi")
    }
  }

  const features = [
    { icon: Zap, title: ct.feature_1_title, desc: ct.feature_1_desc, color: "text-yellow-500" },
    { icon: Headphones, title: ct.feature_2_title, desc: ct.feature_2_desc, color: "text-blue-500" },
    { icon: Shield, title: ct.feature_3_title, desc: ct.feature_3_desc, color: "text-green-500" },
    { icon: MessageCircle, title: ct.feature_4_title, desc: ct.feature_4_desc, color: "text-purple-500" },
  ]

  const faqs = [
    { q: ct.faq_1_q, a: ct.faq_1_a },
    { q: ct.faq_2_q, a: ct.faq_2_a },
    { q: ct.faq_3_q, a: ct.faq_3_a },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary/10 via-background to-primary/5 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
              {ct.hero_badge}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">{ct.hero_title}</h1>
            <p className="text-lg md:text-xl text-muted-foreground">{ct.hero_subtitle}</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-2xl mx-auto mt-12">
            <div className="text-center p-4 rounded-2xl bg-card border shadow-sm">
              <div className="text-2xl md:text-3xl font-bold text-primary">{ct.stats_response_value}</div>
              <div className="text-sm text-muted-foreground">{ct.stats_response}</div>
            </div>
            <div className="text-center p-4 rounded-2xl bg-card border shadow-sm">
              <div className="text-2xl md:text-3xl font-bold text-primary">{ct.stats_support_value}</div>
              <div className="text-sm text-muted-foreground">{ct.stats_support}</div>
            </div>
            <div className="text-center p-4 rounded-2xl bg-card border shadow-sm">
              <div className="text-2xl md:text-3xl font-bold text-primary">{ct.stats_satisfaction_value}</div>
              <div className="text-sm text-muted-foreground">{ct.stats_satisfaction}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info & Form Section */}
      <section
        id="contact-form"
        ref={(el) => {
          sectionRefs.current["contact-form"] = el
        }}
        className={`py-16 md:py-24 transition-all duration-700 ${visibleSections.has("contact-form") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{ct.contact_info_title}</h2>
              <p className="text-muted-foreground mb-8">{ct.contact_info_subtitle}</p>

              <div className="space-y-6">
                <Card className="p-6 flex items-start gap-4 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{ct.phone_label}</h3>
                    <p className="text-muted-foreground">{siteSettings?.phone || "+998 90 123 45 67"}</p>
                  </div>
                </Card>

                <Card className="p-6 flex items-start gap-4 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{ct.email_label}</h3>
                    <p className="text-muted-foreground">{siteSettings?.email || "info@tabiatshop.uz"}</p>
                  </div>
                </Card>

                <Card className="p-6 flex items-start gap-4 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{ct.address_label}</h3>
                    <p className="text-muted-foreground">{siteSettings?.address || "Toshkent, O'zbekiston"}</p>
                  </div>
                </Card>

                <Card className="p-6 flex items-start gap-4 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{ct.working_hours}</h3>
                    <p className="text-muted-foreground">{ct.working_hours_value}</p>
                  </div>
                </Card>
              </div>
            </div>

            {/* Form */}
            <div>
              <Card className="p-8">
                <h2 className="text-2xl font-bold mb-2">{ct.form_title}</h2>
                <p className="text-muted-foreground mb-6">{ct.form_subtitle}</p>

                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{ct.form_success_title}</h3>
                    <p className="text-muted-foreground mb-6">{ct.form_success_text}</p>
                    <Button
                      onClick={() => {
                        setIsSubmitted(false)
                        setFormData({ name: "", phone: "" })
                        setError("")
                      }}
                      variant="outline"
                    >
                      {ct.form_send_another}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">{ct.form_name}</label>
                      <Input
                        placeholder={ct.form_name_placeholder}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">{ct.form_phone}</label>
                      <Input
                        type="tel"
                        placeholder={ct.form_phone_placeholder}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="h-12 rounded-xl"
                        required
                      />
                    </div>

                    {error && <p className="text-sm text-destructive text-center">{error}</p>}

                    <Button type="submit" className="w-full h-12 rounded-xl text-base" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                          {ct.form_submitting}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {ct.form_submit}
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        ref={(el) => {
          sectionRefs.current["features"] = el
        }}
        className={`py-16 bg-muted/50 transition-all duration-700 ${visibleSections.has("features") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
      >
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">{ct.features_title}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className={`w-14 h-14 rounded-xl bg-muted flex items-center justify-center mx-auto mb-4`}>
                  <feature.icon className={`w-7 h-7 ${feature.color}`} />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faq"
        ref={(el) => {
          sectionRefs.current["faq"] = el
        }}
        className={`py-16 md:py-24 transition-all duration-700 ${visibleSections.has("faq") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
      >
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">{ct.faq_title}</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <Card
                key={i}
                className="overflow-hidden cursor-pointer"
                onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
              >
                <div className="p-5 flex items-center justify-between">
                  <h3 className="font-medium pr-4">{faq.q}</h3>
                  <ChevronDown
                    className={`w-5 h-5 text-muted-foreground transition-transform flex-shrink-0 ${expandedFaq === i ? "rotate-180" : ""
                      }`}
                  />
                </div>
                {expandedFaq === i && (
                  <div className="px-5 pb-5 pt-0 text-muted-foreground border-t mt-2 pt-4">{faq.a}</div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">{ct.cta_title}</h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">{ct.cta_text}</p>
          <Button
            size="lg"
            variant="secondary"
            className="rounded-full px-8"
            onClick={() => window.open(`tel:${siteSettings?.phone || "+998901234567"}`)}
          >
            <Phone className="w-5 h-5 mr-2" />
            {ct.cta_button}
          </Button>
        </div>
      </section>
    </div>
  )
}
